import type { FormBlock, FormField, FormSchema } from "@/lib/database";

import { createTableColumn, TABLE_COLUMN_TYPES, TABLE_MAX_ROWS_LIMIT } from "@/lib/fields/field-config";
import { ensureDefaultFormSettings } from "./form-defaults";

// The AI-builder returns inconsistent shapes: field id key is `id` vs
// `name`, the fields container is `fields` vs `steps` vs `blocks`, the title is
// `settings.title` vs top-level `formTitle`. Normalize any raw LLM output into
// the canonical FormSchema shape the preview/editor expect.

const AI_TYPE_ALIASES: Record<string, FormField["type"]> = {
	text: "text",
	input: "text",
	"short-text": "text",
	short_text: "text",
	"short-answer": "text",
	string: "text",
	textarea: "textarea",
	"long-text": "textarea",
	long_text: "textarea",
	"long-answer": "textarea",
	paragraph: "textarea",
	message: "textarea",
	email: "email",
	"email-address": "email",
	email_address: "email",
	number: "number",
	numeric: "number",
	integer: "number",
	phone: "phone",
	tel: "phone",
	telephone: "phone",
	date: "date",
	datetime: "date",
	"date-time": "date",
	time: "time",
	select: "select",
	dropdown: "select",
	"drop-down": "select",
	radio: "radio",
	"multiple-choice": "radio",
	multiple_choice: "radio",
	multichoice: "radio",
	single_choice: "radio",
	checkbox: "checkbox",
	checkboxes: "checkbox",
	"multi-select": "checkbox",
	multi_select: "checkbox",
	multiselect: "checkbox",
	rating: "rating",
	stars: "rating",
	scale: "rating",
	statement: "statement",
	heading: "statement",
	note: "statement",
	url: "link",
	link: "link",
	website: "link",
	address: "address",
	file: "file",
	upload: "file",
	signature: "signature",
};

const CANONICAL_TYPES = new Set<FormField["type"]>([
	"text",
	"email",
	"textarea",
	"radio",
	"checkbox",
	"number",
	"select",
	"slider",
	"tags",
	"social",
	"date",
	"signature",
	"file",
	"poll",
	"rating",
	"time",
	"scheduler",
	"statement",
	"phone",
	"address",
	"link",
	"banner",
	"field-group",
	"table",
]);

// Flat field container keys the LLM might use (NOT `blocks`/`steps` — those are
// handled as multi-step blocks below).
const CONTAINER_KEYS = ["fields", "questions", "formFields", "form_fields", "elements", "inputs"];

// Some models wrap the whole schema in an extra object: { "form": { ... } }.
const WRAPPER_KEYS = ["form", "schema", "formSchema", "form_schema", "data", "result"];

function slugify(value: unknown): string {
	const s = String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9а-яё]+/gi, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 32);
	return s || "field";
}

function asString(value: unknown): string {
	return typeof value === "string" ? value.trim() : "";
}

function normalizeType(raw: unknown): FormField["type"] {
	const key = asString(raw).toLowerCase().replace(/\s+/g, "_");
	const aliased = AI_TYPE_ALIASES[key];
	if (aliased) return aliased;
	if (CANONICAL_TYPES.has(key as FormField["type"])) {
		return key as FormField["type"];
	}
	return "text";
}

function normalizeTableSettings(
	f: Record<string, unknown>,
): NonNullable<FormField["settings"]> {
	const rawCols = f.columns ?? f.tableColumns ?? f.table_columns;
	const columns = Array.isArray(rawCols)
		? rawCols
				.map((c: unknown, j: number) => normalizeField(c, j))
				.filter((c) =>
					(TABLE_COLUMN_TYPES as readonly string[]).includes(c.type),
				)
		: [];
	const rawMaxRows = Number(f.maxRows ?? f.tableMaxRows);
	const maxRows = Number.isFinite(rawMaxRows)
		? Math.min(TABLE_MAX_ROWS_LIMIT, Math.max(1, rawMaxRows))
		: 10;
	return {
		tableOrientation: "columns",
		tableColumns: columns.length > 0 ? columns : [createTableColumn("text")],
		tableMaxRows: maxRows,
	};
}

function normalizeField(raw: unknown, index: number): FormField {
	const f = raw && typeof raw === "object" ? (raw as Record<string, any>) : {};

	const type = normalizeType(f.type ?? f.fieldType ?? f.field_type);
	const label =
		asString(f.label ?? f.title ?? f.question ?? f.name) || `Field ${index + 1}`;
	const baseId = asString(f.id ?? f.name ?? f.key ?? label) || `field_${index + 1}`;

	const field: FormField = {
		id: slugify(baseId),
		type,
		label,
		required: Boolean(
			f.required ?? f.isRequired ?? f.mandatory ?? f.is_required ?? false
		),
	};

	const placeholder = f.placeholder ?? f.hint ?? f.helpText ?? f.help_text;
	if (asString(placeholder)) field.placeholder = asString(placeholder);

	const description = f.description ?? f.help ?? f.subtitle;
	if (asString(description)) field.description = asString(description);

	const rawOptions = f.options ?? f.choices ?? f.values ?? f.items;
	if (Array.isArray(rawOptions)) {
		const options = rawOptions
			.map((o: unknown) => {
				if (typeof o === "string") return o.trim();
				if (o && typeof o === "object") {
					const rec = o as Record<string, any>;
					return asString(rec.label ?? rec.value) || JSON.stringify(o);
				}
				return String(o);
			})
			.filter((o) => o.length > 0);
		if (options.length > 0) field.options = options;
	}

	if (type === "table") {
		field.settings = {
			...(field.settings ?? {}),
			...normalizeTableSettings(f),
		};
	}

	return field;
}

function dedupeFieldIds(fields: FormField[]): FormField[] {
	const seen = new Set<string>();
	return fields.map((f) => {
		if (!seen.has(f.id)) {
			seen.add(f.id);
			return f;
		}
		let i = 2;
		while (seen.has(`${f.id}_${i}`)) i++;
		const newId = `${f.id}_${i}`;
		seen.add(newId);
		return { ...f, id: newId };
	});
}

function extractStepFields(step: unknown): unknown[] {
	if (!step || typeof step !== "object") return [];
	const s = step as Record<string, any>;
	if (Array.isArray(s.fields)) return s.fields;
	if (Array.isArray(s.questions)) return s.questions;
	return [];
}

function extractBlocks(
	raw: Record<string, any>
): { blocks: FormBlock[]; fields: FormField[] } | null {
	const rawSteps = Array.isArray(raw.blocks)
		? raw.blocks
		: Array.isArray(raw.steps)
			? raw.steps
			: null;
	if (!rawSteps || rawSteps.length === 0) return null;

	const blocks: FormBlock[] = rawSteps.map((step, i) => {
		const s = step && typeof step === "object" ? (step as Record<string, any>) : {};
		const fields = dedupeFieldIds(
			extractStepFields(step).map((f, j) => normalizeField(f, j))
		);
		return {
			id: asString(s.id ?? s.name ?? s.key) || `step-${i + 1}`,
			title: asString(s.title ?? s.label ?? s.name) || `Step ${i + 1}`,
			...(asString(s.description) ? { description: asString(s.description) } : {}),
			fields,
		};
	});

	const fields = dedupeFieldIds(blocks.flatMap((b) => b.fields));
	return { blocks, fields };
}

function flattenFieldContainers(items: unknown[]): unknown[] {
	const out: unknown[] = [];
	for (const it of items) {
		if (it && typeof it === "object") {
			const rec = it as Record<string, any>;
			if (Array.isArray(rec.fields)) {
				out.push(...rec.fields);
				continue;
			}
			if (Array.isArray(rec.questions)) {
				out.push(...rec.questions);
				continue;
			}
		}
		out.push(it);
	}
	return out;
}

function extractFlatFields(raw: Record<string, any>): FormField[] {
	for (const key of CONTAINER_KEYS) {
		const v = raw[key];
		if (Array.isArray(v)) {
			return dedupeFieldIds(
				flattenFieldContainers(v).map((f, i) => normalizeField(f, i))
			);
		}
	}
	return [];
}

function unwrap(raw: Record<string, any>): Record<string, any> {
	const hasSchemaKeys = ["fields", "blocks", "steps", "settings", "questions"].some(
		(k) => k in raw
	);
	if (hasSchemaKeys) return raw;
	for (const k of WRAPPER_KEYS) {
		const v = raw[k];
		if (v && typeof v === "object" && !Array.isArray(v)) {
			return unwrap(v as Record<string, any>);
		}
	}
	return raw;
}

export function normalizeAiSchema(raw: unknown): FormSchema {
	const source = raw && typeof raw === "object" ? (raw as Record<string, any>) : {};
	const unwrapped = unwrap(source);

	const multi = extractBlocks(unwrapped);
	const blocks = multi ? multi.blocks : [];
	const fields = multi ? multi.fields : extractFlatFields(unwrapped);

	const rawSettings =
		unwrapped.settings && typeof unwrapped.settings === "object"
			? (unwrapped.settings as Record<string, any>)
			: {};

	const title =
		asString(rawSettings.title ?? rawSettings.formTitle) ||
		asString(unwrapped.formTitle ?? unwrapped.title ?? unwrapped.name) ||
		"";
	const description =
		asString(rawSettings.description ?? rawSettings.formDescription) ||
		asString(unwrapped.formDescription ?? unwrapped.description) ||
		"";
	const submitText =
		asString(rawSettings.submitText ?? rawSettings.submitButtonText) || "";

	return ensureDefaultFormSettings({
		blocks,
		fields,
		settings: {
			...rawSettings,
			title,
			description,
			submitText,
			multiStep: blocks.length > 1,
		},
	} as FormSchema);
}
